import { getTimezone } from 'countries-and-timezones';
import { getSuggestedCountryCode } from '../profile/countrySuggestion';
import { AppLanguageCode, getAppLanguage } from './languages';

// Multilingual countries without a single default use the device preference or English.
const COUNTRY_LANGUAGE_GROUPS: ReadonlyArray<
  readonly [AppLanguageCode, readonly string[]]
> = [
  ['fr-FR', ['FR', 'MC']],
  ['de-DE', ['DE', 'AT', 'LI']],
  ['ro-RO', ['RO', 'MD']],
  ['ka-GE', ['GE']],
  ['nl-NL', ['NL']],
  ['it-IT', ['IT', 'SM', 'VA']],
  ['el-GR', ['GR', 'CY']],
  [
    'es-ES',
    [
      'ES',
      'MX',
      'AR',
      'BO',
      'CL',
      'CO',
      'CR',
      'CU',
      'DO',
      'EC',
      'GT',
      'HN',
      'NI',
      'PA',
      'PE',
      'PR',
      'PY',
      'SV',
      'UY',
      'VE',
    ],
  ],
  ['pt-PT', ['PT', 'BR', 'AO', 'MZ', 'CV', 'GW', 'ST']],
  ['fi-FI', ['FI']],
  ['nb-NO', ['NO', 'SJ']],
  ['sv-SE', ['SE', 'AX']],
  ['da-DK', ['DK']],
  ['pl-PL', ['PL']],
  ['lv-LV', ['LV']],
  ['lt-LT', ['LT']],
  ['cs-CZ', ['CZ']],
  ['sk-SK', ['SK']],
  ['et-EE', ['EE']],
  ['bn-BD', ['BD']],
  ['ur-PK', ['PK']],
  [
    'ar-AE',
    [
      'AE',
      'SA',
      'EG',
      'DZ',
      'BH',
      'IQ',
      'JO',
      'KW',
      'LB',
      'LY',
      'MA',
      'OM',
      'PS',
      'QA',
      'SD',
      'SY',
      'TN',
      'YE',
    ],
  ],
];

export const resolveGuestLanguage = (
  locales: readonly string[],
  timeZone?: string
): AppLanguageCode => {
  for (const locale of locales) {
    const normalized = locale.replace(/_/g, '-');
    const language =
      getAppLanguage(normalized) ?? getAppLanguage(normalized.split('-')[0]);
    if (language) {
      return language.code;
    }
  }

  const country = getSuggestedCountryCode(timeZone, locales[0]);
  const countryLanguage = (code: string) =>
    COUNTRY_LANGUAGE_GROUPS.find(([, countries]) =>
      countries.includes(code)
    )?.[0];
  if (country) {
    return countryLanguage(country) ?? 'en-GB';
  }

  // A shared time zone is sufficient only when every country uses the same default.
  const countries = timeZone ? getTimezone(timeZone)?.countries : undefined;
  const sharedLanguage = countries?.[0] && countryLanguage(countries[0]);
  return sharedLanguage &&
    countries.every((code) => countryLanguage(code) === sharedLanguage)
    ? sharedLanguage
    : 'en-GB';
};
